import _ from "lodash"
import React, { useState, useRef } from "react"
import {
	Avatar,
	Button,
	Chip,
	Grid,
	IconButton,
	MenuItem,
	TextField,
	Typography,
	Checkbox,
	FormControlLabel,
	InputLabel,
	Tooltip,
	Divider as MuiDivider,
	Link
} from "@material-ui/core"
import {
	Divider,
	ActionDialog,
	Notification,
	InfoDialog,
	Loading,
	PopConfirm,
	Portlet
} from "@/components"

import ApiService from "@/services/Api"
import AnalyticsService from "@/services/Analytics"
import TeamService from "@/services/Team"
import UserInInstanceService from "@/services/UserInInstance"
import HardCoded from "@/services/HardCoded"

import InvitePendingCard from "@/pages/Admin/Attendant/components/InvitePendingCard"
import UserInInstanceCard from "@/pages/Admin/Attendant/components/UserInInstanceCard"
import Breadcrumb from "@/components/BreadcrumbNew"
import UpsellAttendantAlertContainer from "@/pages/Admin/Attendant/components/UpsellAttendantAlertContainer"

import AdminInvitePendingSkeleton from "@/skeletons/Admin/InvitePending"
import ActiveAttendancesSkeleton from "@/skeletons/Admin/ActiveAttendancesSkeleton"

import useValidation, { ErrorType } from "@/hooks/useValidation"
import useDidMount from "@/hooks/useDidMount"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import useContactDisplay from "@/hooks/useContactDisplay"
import { useGlobalStateStore } from "@/store/GlobalState"

import { formatEmail } from "@/utils/mask"
import { getErrorName } from "@/utils/response"
import { onlyLettersNumbersAndAccents } from "@/utils/string"
import { buildChatLink } from "@/utils/chat"

import {
	Team
} from "@/protocols/team"

import {
	Add,
	Person as AttendantIcon,
	Forum as ChatIcon
} from "@material-ui/icons"

import {
	UserInInstance,
	UserInInstanceStatus
} from "@/protocols/userInInstance"
import { ActiveAttendanceDetail, ActiveAttendanceSummaryItem } from "@/protocols/attendance"

import { colors } from "@/styles/colors"
import useCustomStyles from "@/styles/custom"
import useStyles from "@/pages/Admin/Attendant/styles"
import { letalkLinks, whatsappLinks } from "@/utils/link"
import AttendantGroupPermissionGate from "@/components/PermissionGate/AttendantGroupPermissionGate"

type InviteTokenDataProps = {
	openDialog: boolean
	invitation_link?: string
	name?: string
	email?: string
}

type TeamDataProps = Team & {
	instanceId?: number
	selected: boolean
}

type ChangeUserInInstanceData = Omit<UserInInstance, "teams" | "id" | "status"> & {
	id?: number
	status?: UserInInstanceStatus
	teams: TeamDataProps[]
	isOwner: boolean
	role_id?: number
}

type UserInvited = ChangeUserInInstanceData & {
	invitation_link: string
}

export type AttendantAutomaticUpsellBlockCode = "user_reach_limit" | "user_is_reaching_the_limit"

type ActiveChatsDialogState = {
	open: boolean
	loading: boolean
	resolved: boolean
	attendances: ActiveAttendanceDetail[]
	userId: number | null
	userName: string
	actionType: "delete" | "block"
	user: ChangeUserInInstanceData | null
}

const ERROR_NAME_TO_DELETE_MESSAGE: Partial<Record<string, string>> = {
	HaveAttendencesAssociateWithUser: "Existe atendimentos vinculados a esse usuario.",
	InstanceOwnerUserCanNotBeDeleted: "Esse usuário é dono desse Inbox e não pode ser excluído.",
	UserHaveTeams: "Esse usuário possui uma ou mais equipes e por isso não pode ser excluído"
}

const ACTION_TYPE_CONFIG: Record<"delete" | "block", {
	actionLabel: string
	saveLabel: string
	actionVerb: string
}> = {
	delete: {
		actionLabel: "Excluir",
		saveLabel: "EXCLUIR",
		actionVerb: "excluir"
	},
	block: {
		actionLabel: "Bloquear",
		saveLabel: "BLOQUEAR",
		actionVerb: "bloquear"
	}
}

const Attendant: React.FC = () => {
	const customClasses = useCustomStyles()
	const {
		validation,
		clearValidation,
		triggerValidation
	} = useValidation()

	const globalStateStore = useGlobalStateStore()

	const instanceRoles = globalStateStore.instance.roles

	const classes = useStyles()
	const userQuantityLimit = useSubscriptionLimits("quantity", "user")
	const { getDisplayName, getDisplayPhoneNumber } = useContactDisplay()

	const [inviteTokenData, setInviteTokenData] = useState<InviteTokenDataProps>({
		openDialog: false
	})

	const [usersInInstance, setUsersInInstance] = useState<ChangeUserInInstanceData[]>([])
	const [usersInInstanceLoading, setUsersInInstanceLoading] = useState<boolean>(false)
	const [activeAttendanceSummary, setActiveAttendanceSummary] = useState<Record<number, number>>({})

	const [usersInvited, setUsersInvited] = useState<UserInvited[]>([])
	const [usersInvitedLoading, setUsersInvitedLoading] = useState<boolean>(false)

	const [activeChatsDialog, setActiveChatsDialog] = useState<ActiveChatsDialogState>({
		open: false,
		loading: false,
		resolved: false,
		attendances: [],
		userId: null,
		userName: "",
		actionType: "delete",
		user: null
	})
	const fetchIdRef = useRef(0)
	const summaryLoadedRef = useRef(false)

	const totalAttendantsCreated = usersInvited?.length + usersInInstance?.length

	const userQuantityLimitOnSubscription = globalStateStore.instance.subscriptionData?.limits?.quantity?.user || 0

	const getAttendantUpsellBlockCode = (): AttendantAutomaticUpsellBlockCode | null => {
		if (userQuantityLimit.mustBlock) {
			return "user_reach_limit"
		}

		const diffBetweenSubscriptionUserLimitsAndCreatedUsers = userQuantityLimitOnSubscription - totalAttendantsCreated

		if (diffBetweenSubscriptionUserLimitsAndCreatedUsers === 1) {
			return "user_is_reaching_the_limit"
		}

		return null
	}

	const attendantUpsellBlockCode = getAttendantUpsellBlockCode()

	const isAdminUserReachedSubscriptionUserQuantityLimit = attendantUpsellBlockCode !== null

	const isWhatsappChannel = globalStateStore.currentChannel?.channelType === "whatsapp"

	const canShowAutomaticUpsellAlertForAdminUser = isAdminUserReachedSubscriptionUserQuantityLimit && isWhatsappChannel

	const [userInInstance, setUserInInstance] = useState<ChangeUserInInstanceData>({
		name: "",
		email: "",
		teams: [],
		isOwner: false,
		type: "human"
	})

	const [openedUserInInstanceDialog, setOpenedUserInInstanceDialog] = useState<boolean>(false)
	const [userInInstanceLoading, setUserInInstanceLoading] = useState<boolean>(false)

	const [teams, setTeams] = useState<TeamDataProps[]>([])

	const inviteDialogOpen = (invitation_link: string, name: string, email: string) => {
		setInviteTokenData({
			openDialog: true,
			invitation_link,
			name,
			email
		})
		userQuantityLimit.resetLimit()
	}

	const openUserInInstanceDialog = (user: ChangeUserInInstanceData) => {
		setUserInInstance({
			...user,
			teams: teams.map(team => ({
				...team,
				selected: _.findIndex(user.teams, { id: team.id }) >= 0
			}))
		})

		setOpenedUserInInstanceDialog(true)
	}

	const handleUserInInstanceDataChange = (key: keyof ChangeUserInInstanceData, value: unknown) => {
		clearValidation(key)

		setUserInInstance({
			...userInInstance,
			[key]: value
		})
	}

	const getUsersInvited = async () => {
		setUsersInvitedLoading(true)

		try {
			const { data } = await ApiService.get("/users/invited")

			setUsersInvited(data)
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		setUsersInvitedLoading(false)
	}

	const getUsersInInstance = async () => {
		if (!usersInInstance) {
			setUsersInInstanceLoading(true)
		}

		const data = await UserInInstanceService.getUsersInInstance({
			status: ["active", "blocked"],
			type: ["human"]
		})

		if (data) {
			setUsersInInstance(data as ChangeUserInInstanceData[])
		}

		setUsersInInstanceLoading(false)
	}

	const handleUserInInstanceInvitationSave = async () => {
		setUserInInstanceLoading(true)

		try {
			const userInInstanceTeams = userInInstance.teams
			const userInInstanceTeamsFiltered = userInInstanceTeams.filter(team => team.selected)

			const { data } = await ApiService.post("/user", {
				name: userInInstance.name,
				email: userInInstance.email,
				teams: userInInstanceTeamsFiltered.map(team => team.id),
				role_id: userInInstance?.role_id
			})

			setOpenedUserInInstanceDialog(false)

			inviteDialogOpen(
				data.invitation_link,
				(userInInstance.name as string),
				(userInInstance.email as string)
			)

			getUsersInvited()
			getUsersInInstance()

			Notification.success({ message: "O convite foi gerado com sucesso!" })
		} catch (error) {
			const errorName = getErrorName(error as ErrorType)

			if (errorName === "invitation_request_already_exists_for_this_email") {
				Notification.warning({ message: "Já existe uma solicitação criada para esse email" })
				return
			} else {
				triggerValidation(error as ErrorType)
			}
		}

		setUserInInstanceLoading(false)
	}

	const getTeams = async () => {
		try {
			const teams = await TeamService.getTeams()

			setTeams(teams as TeamDataProps[])
		} catch (error) {
			triggerValidation(error as ErrorType)
		}
	}

	const getActiveAttendancesSummary = async () => {
		const data = await UserInInstanceService.getActiveAttendancesSummary()
		if (data) {
			const map = data.reduce<Record<number, number>>((acc, item: ActiveAttendanceSummaryItem) => {
				acc[item.userId] = item.count
				return acc
			}, {})
			setActiveAttendanceSummary(map)
			summaryLoadedRef.current = true
		}
	}

	const handleTeamCheckboxSelected = (teamIndex: number) => {
		const userInInstanceTeams = userInInstance.teams

		userInInstanceTeams[teamIndex].selected = !userInInstanceTeams[teamIndex].selected

		setUserInInstance({
			...userInInstance,
			teams: userInInstanceTeams
		})
	}

	const handleUserInInstanceSave = async () => {
		setUserInInstanceLoading(true)

		try {
			const userInInstanceTeams = userInInstance.teams
			const userInInstanceTeamsFiltered = userInInstanceTeams.filter(team => team.selected)

			await ApiService
				.put(`/user/${userInInstance.id}`, {
					name: userInInstance.name,
					email: userInInstance.email,
					teams: userInInstanceTeamsFiltered.map(team => team.id),
					role_id: userInInstance?.role_id
				})

			setOpenedUserInInstanceDialog(false)

			getUsersInInstance()

			Notification.success({ message: "O usuário atualizado com sucesso!" })
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		setUserInInstanceLoading(false)
	}

	const handleUserInInstanceClose = async () => {
		setOpenedUserInInstanceDialog(false)
	}

	const openConfirm = (user: ChangeUserInInstanceData, actionType: "delete" | "block") => {
		const isBlocked = user.status === "blocked"
		const entityLabel = user.status === "invited" ? "convite" : "usuário"
		const title = actionType === "delete"
			? `Excluir ${entityLabel}`
			: `${isBlocked ? "Desbloquear" : "Bloquear"} usuário`
		const confirmButtonText = actionType === "delete"
			? "EXCLUIR"
			: (isBlocked ? "DESBLOQUEAR" : "BLOQUEAR")

		PopConfirm.open({
			title,
			description: "Tem certeza? Essa ação é irreversível.",
			confirmButtonText,
			onConfirm: async () => {
				try {
					if (actionType === "delete") {
						await ApiService.delete(`/user/${user.id}`)
						getUsersInvited()
						getUsersInInstance()
						userQuantityLimit.resetLimit()
					} else {
						await ApiService.put(`/user/${user.id}/block`)
						getUsersInInstance()
					}
				} catch (error) {
					if (actionType === "delete") {
						const errorName = getErrorName(error as ErrorType)
						const warningMessage = ERROR_NAME_TO_DELETE_MESSAGE[errorName]
						if (warningMessage) {
							Notification.warning({ message: warningMessage })
						} else {
							triggerValidation(error as ErrorType)
						}
					} else {
						triggerValidation(error as ErrorType)
					}
				}
			}
		})
	}

	const openActiveChatsDialog = async (user: ChangeUserInInstanceData, actionType: "delete" | "block") => {
		const fetchId = ++fetchIdRef.current
		setActiveChatsDialog({
			open: true,
			loading: true,
			resolved: false,
			attendances: [],
			userId: user.id || null,
			userName: user.name || "",
			actionType,
			user
		})
		try {
			const { data } = await ApiService.get(`/users/${user.id}/active-attendances`)
			if (fetchId !== fetchIdRef.current) return
			setActiveChatsDialog(prev => ({ ...prev, loading: false, attendances: data }))
		} catch (_error) {
			if (fetchId !== fetchIdRef.current) return
			setActiveChatsDialog(prev => ({ ...prev, loading: false }))
		}
	}

	const handleDeleteUser = (user: ChangeUserInInstanceData) => {
		const canAutoFinish = HardCoded.checkFeatureFlag("canAutoFinishAttendancesOnUserDeletion")
		const activeCount = summaryLoadedRef.current ? (activeAttendanceSummary[user.id || 0] || 0) : 0
		if (canAutoFinish && activeCount > 0) {
			openActiveChatsDialog(user, "delete")
			return
		}
		openConfirm(user, "delete")
	}

	const handleBlockUser = (user: ChangeUserInInstanceData) => {
		if (user.status !== "blocked") {
			const canAutoFinish = HardCoded.checkFeatureFlag("canAutoFinishAttendancesOnUserDeletion")
			const activeCount = summaryLoadedRef.current ? (activeAttendanceSummary[user.id || 0] || 0) : 0
			if (canAutoFinish && activeCount > 0) {
				openActiveChatsDialog(user, "block")
				return
			}
		}
		openConfirm(user, "block")
	}

	const handleRefreshDialogAttendances = async () => {
		if (!activeChatsDialog.userId) return
		const fetchId = ++fetchIdRef.current
		setActiveChatsDialog(prev => ({ ...prev, loading: true }))
		try {
			const { data } = await ApiService.get(`/users/${activeChatsDialog.userId}/active-attendances`)
			if (fetchId !== fetchIdRef.current) return
			const attendances = data as ActiveAttendanceDetail[]
			if (attendances.length === 0) {
				getActiveAttendancesSummary()
				setActiveChatsDialog(prev => ({ ...prev, loading: false, attendances: [], resolved: true }))
			} else {
				setActiveChatsDialog(prev => ({ ...prev, loading: false, attendances }))
			}
		} catch (_error) {
			if (fetchId !== fetchIdRef.current) return
			setActiveChatsDialog(prev => ({ ...prev, loading: false }))
		}
	}

	const handleConfirmAction = async () => {
		if (!activeChatsDialog.user) return
		const { user, actionType } = activeChatsDialog
		setActiveChatsDialog(prev => ({ ...prev, open: false }))
		try {
			if (actionType === "delete") {
				await ApiService.delete(`/user/${user.id}`)
				getUsersInvited()
				getUsersInInstance()
				userQuantityLimit.resetLimit()
			} else {
				await ApiService.put(`/user/${user.id}/block`)
				getUsersInInstance()
			}
		} catch (error) {
			if (actionType === "delete") {
				const errorName = getErrorName(error as ErrorType)
				const warningMessage = ERROR_NAME_TO_DELETE_MESSAGE[errorName]
				if (warningMessage) {
					Notification.warning({ message: warningMessage })
				} else {
					triggerValidation(error as ErrorType)
				}
			} else {
				triggerValidation(error as ErrorType)
			}
		}
	}

	const handleGetDialogUserCreation = () => {
		const isUserLimitReached = userQuantityLimit.mustBlock
		const isUserLimitIgnored = HardCoded.checkFeatureFlag("canIgnoreAttendantQuantityLimit")
		const isLimitBlocking = isUserLimitReached && !isUserLimitIgnored

		if (isLimitBlocking) {
			window.open(whatsappLinks.sendMessageToUpgradeAttendantsLimit, "_blank")
			return
		}

		openUserInInstanceDialog({
			name: "",
			email: "",
			teams: [],
			isOwner: false,
			type: "human"
		})
	}

	useDidMount(() => {
		AnalyticsService.capture("attendants_page_opened")
		getUsersInvited()
		getUsersInInstance()
		getTeams()
		if (HardCoded.checkFeatureFlag("canAutoFinishAttendancesOnUserDeletion")) {
			getActiveAttendancesSummary()
		}
	})

	const { actionLabel, saveLabel, actionVerb } = ACTION_TYPE_CONFIG[activeChatsDialog.actionType]
	const dialogTitle = activeChatsDialog.resolved ? `${actionLabel} usuário` : "Atendimentos vinculados"
	const saveButtonText = activeChatsDialog.resolved ? saveLabel : "JÁ RESOLVI"
	const descriptionText = activeChatsDialog.loading
		? "Carregando atendimentos..."
		: `${activeChatsDialog.userName} possui ${activeChatsDialog.attendances.length} atendimento(s) vinculado(s). Transfira ou finalize-os antes de ${actionVerb}.`

	return (
		<>
			<Breadcrumb
				data={[
					{ name: "Atendentes", pathname: "/admin/attendant" }
				]}
			/>

			<Divider orientation="horizontal" size={2} />

			<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
				<Grid item>
					<Grid
						container
						alignItems="center"
						spacing={2}
					>
						<Grid
							item
						>
							<Typography
								variant="h5"
								className={classes.titleText}
							>
								Atendentes
							</Typography>
						</Grid>

						<Grid
							item
						>
							<Tooltip
								title="Quantidade de atendentes cadastrados"
								placement="right"
							>
								<Grid
									className={classes.attendantToggle}
									style={{
										backgroundColor: attendantUpsellBlockCode === "user_reach_limit" ? colors.yellow[400] : colors.purple[100]
									}}
								>
									<AttendantIcon fontSize="small" style={{ color: attendantUpsellBlockCode === "user_reach_limit" ? colors.gray[600] : colors.getPrimaryColor() }} />
									<Typography className={classes.attendantToggleText}>
										{`${totalAttendantsCreated} de ${userQuantityLimitOnSubscription}`}
									</Typography>
								</Grid>
							</Tooltip>
						</Grid>
					</Grid>
				</Grid>
				<Grid item>
					<AttendantGroupPermissionGate action="invite">
						<Button
							variant="contained"
							color="primary"
							onClick={handleGetDialogUserCreation}
							startIcon={<Add />}
						>
							Adicionar Novo Atendente
						</Button>
					</AttendantGroupPermissionGate>
				</Grid>
			</Grid>

			<Link
				href={letalkLinks.wikiHowToRegisteringAttendants}
				underline="always"
				target="_blank"
				color="inherit"
			>
				Convide sua equipe para a Letalk agora mesmo
			</Link>

			<Divider size={6} orientation="horizontal" />

			{canShowAutomaticUpsellAlertForAdminUser && (
				<>
					<UpsellAttendantAlertContainer
						attendantUpsellBlockCode={attendantUpsellBlockCode}
					/>

					<Divider size={6} orientation="horizontal" />
				</>
			)}

			<Loading
				loading={usersInvitedLoading}
				customLoadingElement={<AdminInvitePendingSkeleton />}
			>
				<Grid container>
					{
						usersInvited.length
							? (
								<>
									{usersInvited.map((user, index) => (
										<Grid item xs={12} key={index}>
											<InvitePendingCard
												invitedUserId={Number(user.id)}
												name={user.name}
												email={user.email}
												inviteLink={user.invitation_link}
												handleDelete={() => handleDeleteUser(user)}
											/>

											<Divider size={2} orientation="horizontal" />
										</Grid>
									))}

									<Divider size={2} orientation="horizontal" />

									<Grid item xs={12}>
										<MuiDivider />
									</Grid>
								</>
							) : null
					}
				</Grid>
			</Loading>

			<Divider size={3} orientation="horizontal" />

			<Loading
				loading={usersInInstanceLoading}
				customLoadingElement={<AdminInvitePendingSkeleton />}>
				<Grid container>
					{
						usersInInstance.length
							? usersInInstance.map((userInInstance, index) => (
								<Grid item xs={12} key={index}>
									<UserInInstanceCard
										name={userInInstance.name}
										email={userInInstance.email}
										status={userInInstance.status}
										currentRoleName={userInInstance.current_role_name}
										isOwner={userInInstance.isOwner}
										teams={userInInstance.teams}
										onClickEditButton={() => openUserInInstanceDialog(userInInstance)}
										onClickDeleteButton={() => handleDeleteUser(userInInstance)}
										onClickBlockButton={() => handleBlockUser(userInInstance)}
									/>

									<Divider size={2} orientation="horizontal" />
								</Grid>
							))
							: <Grid item xs={12}>
								<Typography align="center" variant="h2">
									Nenhum membro cadastrado
								</Typography>
							</Grid>
					}
				</Grid>
			</Loading>

			<ActionDialog
				id="edit-or-invite-new-attendant"
				saveButtonId="edit-or-invite-new-attendant-save-button"
				title={userInInstance?.id ? "Editar Atendente" : "Convidar novo membro"}
				onSave={() => userInInstance?.id ? handleUserInInstanceSave() : handleUserInInstanceInvitationSave()}
				saveText={userInInstance?.id ? "SALVAR" : "CONVIDAR"}
				onClose={handleUserInInstanceClose}
				openDialog={openedUserInInstanceDialog}
				loading={userInInstanceLoading}
				fullWidth
			>
				<Grid container>
					<Grid item xs={12}>
						<InputLabel className={customClasses.inputLabel}>
							Nome
						</InputLabel>

						<Divider orientation="horizontal" size={1} />
						<TextField
							id="input-name"
							name="name"
							value={userInInstance.name}
							onChange={
								({ target }) => handleUserInInstanceDataChange("name",
									onlyLettersNumbersAndAccents(target.value))
							}
							variant="outlined"
							placeholder="Ex: José Amilton"
							fullWidth
							helperText={validation.name}
							error={!!validation.name}
						/>
					</Grid>

					<Divider size={2} orientation="horizontal" />

					<Grid item xs={12}>
						<InputLabel className={customClasses.inputLabel}>
							Email
						</InputLabel>

						<Divider orientation="horizontal" size={1} />
						<TextField
							id="input-email"
							name="email"
							value={userInInstance.email}
							onChange={
								({ target }) => handleUserInInstanceDataChange("email", formatEmail(target.value))
							}
							variant="outlined"
							placeholder="example@email.com"
							fullWidth
							helperText={validation.email}
							error={!!validation.email}
							disabled={userInInstance.isOwner}
						/>
					</Grid>

					<Divider size={2} orientation="horizontal" />

					<Grid item xs={12}>
						<InputLabel className={customClasses.inputLabel}>
							Cargo do usuário
						</InputLabel>

						<Divider orientation="horizontal" size={1} />

						<TextField
							id="input-user-type"
							name="role_id"
							select
							placeholder="Cargo do usuário"
							value={userInInstance?.role_id || "no-select"}
							onChange={
								({ target }) => target.value !== "no-select" && handleUserInInstanceDataChange("role_id", target.value)
							}
							variant="outlined"
							fullWidth
							helperText={validation.role_id}
							error={!!validation.role_id}
							disabled={userInInstance.isOwner}
						>
							<MenuItem value={"no-select"} disabled>
								Cargo do usuário
							</MenuItem>

							{instanceRoles.map((role) => (
								<MenuItem key={role.id} value={role.id}>
									{role.name}
								</MenuItem>
							))}
						</TextField>
					</Grid>

					<Divider size={3} orientation="horizontal" />

					<Grid item xs={12}>
						<Typography
							color="textPrimary"
							variant="h2"
							className={classes.nameText}
						>
							A quais equipes o atendente pertence?
						</Typography>

						<Grid container className={classes.teamsListContainer}>
							{
								userInInstance.teams.length
									? userInInstance.teams.map((team, index) => (
										<Grid item xs={12} key={index}>
											<FormControlLabel label={team.name} control={
												<Checkbox
													key={index}
													checked={team.selected}
													onChange={() => handleTeamCheckboxSelected(index)}
												/>
											} />
										</Grid>
									))
									: <Grid item xs={12}>
										<Typography align="center" variant="h2" className={classes.noTeamListed}>
											Nenhuma equipe cadastrada.
										</Typography>
									</Grid>
							}
						</Grid>
					</Grid>
				</Grid>
			</ActionDialog>

			<InfoDialog
				title={"Convite foi gerado!"}
				openDialog={inviteTokenData.openDialog}
			>
				<Grid
					container
					alignContent="center"
					className={classes.copyContainer}
				>
					<Grid item xs={12}>
						<Grid container justify="space-between" alignItems="center">
							<Grid item xs={6}>
								<Typography
									color="textPrimary"
									variant="h2"
								>
									{inviteTokenData.name}
								</Typography>

								<Typography
									color="textPrimary"
									variant="caption"
								>
									{inviteTokenData.email}
								</Typography>
							</Grid>

							<Grid item xs={6}>
								<Grid
									container
									justify={"flex-end"}
								>
									<Chip label="Convite Pendente" className={classes.chipPending} />
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Divider size={2} orientation="horizontal" />

					<Grid
						item
						xs={12}
					>
						<Typography
							color="textPrimary"
							variant="body1"
						>
							O convite do usuário <strong>{inviteTokenData.name}</strong> foi enviado para o email <strong>{inviteTokenData.email?.toLowerCase()}</strong>, é necessário clicar no link enviado por email para acessar a plataforma
						</Typography>
					</Grid>
				</Grid>

			</InfoDialog>

			<ActionDialog
				title={dialogTitle}
				openDialog={activeChatsDialog.open}
				onClose={() => setActiveChatsDialog(prev => ({ ...prev, open: false }))}
				onSave={activeChatsDialog.resolved ? handleConfirmAction : handleRefreshDialogAttendances}
				saveText={saveButtonText}
				cancelText="FECHAR"
				maxWidth="xs"
				fullWidth
			>
				{activeChatsDialog.resolved ? (
					<Typography variant="body1">
						Tem certeza? Essa ação é irreversível.
					</Typography>
				) : (
					<Grid container direction="column" spacing={0}>
						<Grid item>
							<Typography variant="body2" color="textSecondary">
								{descriptionText}
							</Typography>
						</Grid>

						<Divider size={2} orientation="horizontal" />

						<Grid item className={classes.attendanceList}>
							{activeChatsDialog.loading ? (
								<ActiveAttendancesSkeleton itemClassName={classes.attendanceItem} />
							) : (
								activeChatsDialog.attendances.map((activeAttendance) => {
									const displayName = getDisplayName(activeAttendance.contactName ?? undefined, activeAttendance.clientPhone ?? undefined) || "Contato desconhecido"

									return (
										<Portlet
											key={activeAttendance.attendanceId}
											elevation={1}
											className={classes.attendanceItem}
										>
											<Grid container alignItems="center" spacing={2} wrap="nowrap">
												<Grid item>
													<Avatar
														src={activeAttendance.clientPictureUrl || undefined}
														className={classes.attendanceAvatar}
														onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }}
													/>
												</Grid>
												<Grid item xs>
													<Typography variant="body1" className={classes.attendanceName}>
														{displayName}
													</Typography>
													{activeAttendance.clientPhone && (
														<Typography variant="caption" color="textSecondary">
															{getDisplayPhoneNumber(activeAttendance.clientPhone ?? undefined) || activeAttendance.clientPhone}
														</Typography>
													)}
												</Grid>
												{activeAttendance.clientId && (
													<Grid item>
														<Tooltip title="Abrir chat">
															<IconButton
																size="small"
																onClick={() => window.open(buildChatLink(activeAttendance.clientId), "_blank")}
															>
																<ChatIcon fontSize="small" color="primary" />
															</IconButton>
														</Tooltip>
													</Grid>
												)}
											</Grid>
										</Portlet>
									)
								})
							)}
						</Grid>
					</Grid>
				)}
			</ActionDialog>
		</>
	)
}

export default Attendant
